// Shared renderer for Fieldlink SEO product pages. Config-driven so every
// money page stays consistent with the existing marketing design.
import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown, Check } from "lucide-react";
import { SEO } from "@/lib/seo";
import { PublicFooter } from "../FAQ";
import { MarketingHeader as GlobalMarketingHeader } from "@/components/marketing/MarketingHeader";
import { RelatedPages, SeoFaq, faqJsonLd } from "@/components/RelatedPages";

import CivilClusterPage from "./CivilClusterPage";

const ORIGIN = "https://www.fieldlink.co.nz";

// Single global dark navigation — re-exported so legacy imports keep working.
export function MarketingHeader() {
  return <GlobalMarketingHeader />;
}

export function Breadcrumb({ trail }) {
  return (
    <nav aria-label="Breadcrumb" className="max-w-6xl mx-auto px-6 md:px-12 pt-5">
      <ol className="flex flex-wrap items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#52616c]">
        {trail.map((t, i) => (
          <li key={t.label} className="flex items-center gap-1.5">
            {i > 0 && <span className="text-[#52616c]">/</span>}
            {t.to ? <Link to={t.to} className="hover:text-[#a63c0b]">{t.label}</Link> : <span className="text-[#344451]">{t.label}</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export const breadcrumbJsonLd = (trail) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: trail.map((t, i) => ({
    "@type": "ListItem", position: i + 1, name: t.label,
    ...(t.to ? { item: `${ORIGIN}${t.to}` } : {}),
  })),
});

export default function SeoProductPage({ cfg }) {
  if (cfg.clusterKey) return <CivilClusterPage pageKey={cfg.clusterKey} />;
  const trail = [{ label: "Home", to: "/" }, { label: "Software", to: "/civil-construction-software-nz" }, { label: cfg.label }];
  return (
    <div className="web-seo bg-[#f6f5f1] text-[#142635] min-h-screen">
      <SEO title={cfg.title} description={cfg.description} path={cfg.path}
        jsonLd={[faqJsonLd(cfg.faqs), breadcrumbJsonLd(trail)]} />
      <MarketingHeader />
      <Breadcrumb trail={trail} />

      {/* hero */}
      <section className="bg-[#f6f5f1] text-[#142635] border-b border-[#cbd3d5] py-16 md:py-24 mt-5">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="block text-[11px] font-mono font-bold uppercase tracking-[0.28em] text-[#a63c0b] mb-4">{cfg.badge || "Built in NZ · Built for Civil"}</div>
          <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-none mb-5">
            {cfg.h1a}<br /><span className="text-[#a63c0b]">{cfg.h1b}</span>
          </h1>
          <p className="text-[#465562] text-base sm:text-lg max-w-2xl">{cfg.heroCopy}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register" className="rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] font-bold uppercase tracking-wider px-6 py-3" data-testid="seo-cta-primary">{cfg.cta || "Start free trial"}</Link>
            <Link to="/pricing" className="rounded-md border border-[#cbd3d5] bg-white hover:bg-white text-[#142635] font-bold uppercase tracking-wider px-6 py-3">See pricing</Link>
          </div>
        </div>
      </section>

      {/* workflow */}
      {cfg.workflow && (
        <section className="max-w-6xl mx-auto px-6 md:px-12 py-14">
          <h2 className="font-display font-black uppercase tracking-tight text-2xl mb-6">{cfg.workflowHeading || "How it works in Fieldlink"}</h2>
          <div className="flex flex-wrap items-center gap-2" data-testid="seo-workflow">
            {cfg.workflow.map((w, i) => (
              <div key={w} className="flex items-center gap-2">
                <span className="rounded-md border border-[#cbd3d5] bg-white text-[#142635] text-[11px] font-bold uppercase tracking-widest px-3.5 py-2">{w}</span>
                {i < cfg.workflow.length - 1 && <ArrowRight className="w-4 h-4 text-[#a63c0b] hidden sm:block" />}
                {i < cfg.workflow.length - 1 && <ArrowDown className="w-4 h-4 text-[#a63c0b] sm:hidden" />}
              </div>
            ))}
          </div>
          <p className="mt-5 text-[#465562]">Keep this workflow connected to the wider <Link to="/job-management-nz" className="underline underline-offset-4">job management workspace</Link>, where the office and field follow the same job record.</p>
        </section>
      )}

      {/* problem / positioning */}
      {cfg.problem && (
        <section className="bg-[#e9eeec] border-y border-[#cbd3d5] py-14">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <h2 className="font-display font-black uppercase tracking-tight text-2xl mb-4">{cfg.problem.heading}</h2>
            {cfg.problem.body.map((p) => <p key={p.slice(0, 30)} className="text-[#465562] leading-relaxed mb-3">{p}</p>)}
          </div>
        </section>
      )}

      {/* content sections */}
      {(cfg.sections || []).map((s) => (
        <section key={s.heading} className="max-w-6xl mx-auto px-6 md:px-12 py-12">
          <h2 className="font-display font-black uppercase tracking-tight text-2xl mb-3">{s.heading}</h2>
          {s.body && <p className="text-[#465562] leading-relaxed max-w-3xl mb-5">{s.body}</p>}
          {s.bullets && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {s.bullets.map((b) => (
                <div key={b} className="flex items-start gap-2.5 rounded-md border border-[#cbd3d5] bg-white px-4 py-3">
                  <Check className="w-4 h-4 text-[#a63c0b] shrink-0 mt-0.5" strokeWidth={3} />
                  <span className="text-sm font-semibold text-[#344451]">{b}</span>
                </div>
              ))}
            </div>
          )}
          {s.example && (
            <pre className="rounded-lg bg-white border border-[#cbd3d5] border-l-4 border-l-[#FF5F15] text-[#344451] text-xs sm:text-sm font-mono px-5 py-4 mt-5 overflow-x-auto whitespace-pre-wrap leading-relaxed">{s.example}</pre>
          )}
        </section>
      ))}

      {/* who it's for */}
      {cfg.forWho && (
        <section className="max-w-6xl mx-auto px-6 md:px-12 py-10">
          <h2 className="font-display font-black uppercase tracking-tight text-2xl mb-5">Who it&apos;s for</h2>
          <div className="flex flex-wrap gap-2">
            {cfg.forWho.map((w) => <span key={w} className="rounded-md bg-white border border-[#cbd3d5] text-[#465562] text-xs font-bold uppercase tracking-wider px-3 py-1.5">{w}</span>)}
          </div>
        </section>
      )}

      <SeoFaq faqs={cfg.faqs} />

      {/* CTA band */}
      <section className="bg-[#e9eeec] border-y border-[#cbd3d5] text-[#142635] py-14">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display font-black uppercase tracking-tight text-3xl mb-3">{cfg.ctaBand || "Run your civil work in one system"}</h2>
          <p className="text-[#52616c] mb-6">Takeoff → estimate → quote → job → crew & plant → field → safety & QA → cost → invoice. 7-day free trial, no credit card.</p>
          <Link to="/register" className="inline-block rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] font-bold uppercase tracking-wider px-8 py-3" data-testid="seo-cta-band">{cfg.cta || "Start free trial"}</Link>
        </div>
      </section>

      {/* related tools & guides */}
      {cfg.tools && (
        <section className="max-w-6xl mx-auto px-6 md:px-12 py-12">
          <h2 className="font-display font-black uppercase tracking-tight text-2xl mb-5">Related Fieldlink tools</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {cfg.tools.map((t) => (
              <Link key={t.to} to={t.to} className="group flex items-center justify-between rounded-md border border-[#cbd3d5] bg-white hover:border-[#FF5F15]/60 px-4 py-3 text-sm font-bold uppercase tracking-wide text-[#344451]">
                {t.label}<ArrowRight className="w-4 h-4 text-[#52616c] group-hover:text-[#a63c0b]" />
              </Link>
            ))}
          </div>
        </section>
      )}

      <RelatedPages current={cfg.path} />
      <PublicFooter />
    </div>
  );
}
